import { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Divider,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaymentIcon from "@mui/icons-material/Payment";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import HistoryIcon from "@mui/icons-material/History";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { limpiarTelefono, abrirWhatsApp } from "./waUtils";

export default function CreditoClienteDetalle({ cliente, store, onClose }) {
  const [showPagoForm, setShowPagoForm] = useState(false);
  const [pago, setPago] = useState({ monto: "", metodo: "efectivo", nota: "" });
  const [error, setError] = useState("");

  const moneda = store.config.moneda;
  const pagos = store.pagosDeCliente(cliente.key);
  const pctPagado = cliente.totalDeuda > 0 ? (cliente.totalPagado / cliente.totalDeuda) * 100 : 0;

  const handleRegistrarPago = () => {
    const monto = parseFloat(pago.monto);
    if (!monto || monto <= 0) {
      setError("Ingresa un monto válido");
      return;
    }
    // CRITICAL VALIDATION: payment cannot exceed remaining debt
    if (monto > cliente.saldoPendiente) {
      setError(`El monto supera la deuda pendiente (${moneda} ${cliente.saldoPendiente.toLocaleString()})`);
      return;
    }

    // Find the oldest unpaid venta for this client
    const ventaTarget = cliente.ventas.find(v => {
      const pagosVenta = (store.pagosCredito || []).filter(p => p.ventaId === v.id);
      const totalPagadoVenta = pagosVenta.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
      return totalPagadoVenta < v.total;
    });

    store.registrarPagoCredito({
      ventaId: ventaTarget?.id || cliente.ventas[0]?.id,
      clienteKey: cliente.key,
      monto,
      metodo: pago.metodo,
      nota: pago.nota,
    });

    setPago({ monto: "", metodo: "efectivo", nota: "" });
    setShowPagoForm(false);
    setError("");
  };

  const handleWhatsApp = () => {
    if (!cliente.telefono) return;
    const tel = limpiarTelefono(cliente.telefono, cliente.codigoPais || store.config.codigoPais);
    const msg = (
      `Hola ${cliente.nombre}, te recordamos que tienes un saldo pendiente de ${moneda} ${cliente.saldoPendiente.toLocaleString()} en ${store.config.nombreNegocio}.`
    );
    if (!abrirWhatsApp(tel, msg)) window.alert("Permite las ventanas emergentes para abrir WhatsApp.");
  };

  return (
    <Box sx={{ p: 2.5, maxHeight: "80vh", overflowY: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{cliente.nombre}</Typography>
          {cliente.telefono && (
            <Typography variant="caption" color="text.secondary">📞 +{cliente.codigoPais || store.config.codigoPais} {cliente.telefono}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* Financial summary card */}
      <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: "background.default", border: 1, borderColor: "grey.200", mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total deuda</Typography>
            <Typography variant="h6" fontWeight={700}>{moneda} {cliente.totalDeuda.toLocaleString()}</Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">Pagado</Typography>
            <Typography variant="h6" fontWeight={700} color="success.main">{moneda} {cliente.totalPagado.toLocaleString()}</Typography>
          </Box>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, pctPagado)}
          color={pctPagado >= 100 ? "success" : pctPagado > 50 ? "warning" : "error"}
          sx={{ height: 8, borderRadius: 4, mb: 1 }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary">
            {Math.round(pctPagado)}% pagado
          </Typography>
          <Typography variant="subtitle1" fontWeight={800} color="error.main">
            Saldo: {moneda} {cliente.saldoPendiente.toLocaleString()}
          </Typography>
        </Stack>
        {cliente.fechaLimite && (
          <Chip
            label={`Fecha límite: ${cliente.fechaLimite}`}
            size="small"
            color={new Date(cliente.fechaLimite) < new Date() ? "error" : "warning"}
            variant="outlined"
            sx={{ mt: 1, fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Quick actions */}
      <Stack direction="row" spacing={1} mb={2}>
        <Button
          variant="contained"
          startIcon={<PaymentIcon />}
          onClick={() => { setShowPagoForm(true); setError(""); }}
          fullWidth
          sx={{ py: 1.2, fontWeight: 700 }}
        >
          Registrar pago
        </Button>
        {cliente.telefono && (
          <>
            <IconButton color="success" onClick={handleWhatsApp} sx={{ border: 1, borderColor: "#25d366" }}>
              <WhatsAppIcon />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => window.open(`tel:+${cliente.codigoPais || store.config.codigoPais}${cliente.telefono}`)}
              sx={{ border: 1, borderColor: "primary.main" }}
            >
              <PhoneIcon />
            </IconButton>
          </>
        )}
      </Stack>

      {/* Payment form */}
      {showPagoForm && (
        <Box sx={{ p: 2, borderRadius: 2, border: 2, borderColor: "primary.main", bgcolor: "background.default", mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            <AddCircleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
            Nuevo pago
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              label="Monto"
              value={pago.monto}
              onChange={(e) => { setPago({ ...pago, monto: e.target.value }); setError(""); }}
              type="number"
              fullWidth
              size="small"
              autoFocus
              helperText={`Máximo: ${moneda} ${cliente.saldoPendiente.toLocaleString()}`}
              error={!!error}
              inputProps={{ max: cliente.saldoPendiente, min: 0.01, step: 0.01 }}
            />
            <TextField
              label="Método"
              value={pago.metodo}
              onChange={(e) => setPago({ ...pago, metodo: e.target.value })}
              select
              fullWidth
              size="small"
            >
              <MenuItem value="efectivo">💵 Efectivo</MenuItem>
              <MenuItem value="transferencia">🏦 Transferencia</MenuItem>
              <MenuItem value="otro">📋 Otro</MenuItem>
            </TextField>
            <TextField
              label="Nota (opcional)"
              value={pago.nota}
              onChange={(e) => setPago({ ...pago, nota: e.target.value })}
              fullWidth
              size="small"
              placeholder="Ej: Abono semanal"
            />

            {error && <Alert severity="error" variant="outlined">{error}</Alert>}

            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={handleRegistrarPago} fullWidth sx={{ fontWeight: 700 }}>
                Confirmar pago
              </Button>
              <Button onClick={() => { setShowPagoForm(false); setError(""); }} sx={{ minWidth: 90 }}>
                Cancelar
              </Button>
            </Stack>

            {/* Quick amount buttons */}
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {[
                { label: "Todo", val: cliente.saldoPendiente },
                { label: "50%", val: Math.round(cliente.saldoPendiente / 2) },
                { label: "25%", val: Math.round(cliente.saldoPendiente / 4) },
              ].filter(b => b.val > 0).map(b => (
                <Chip
                  key={b.label}
                  label={`${b.label}: ${moneda} ${b.val.toLocaleString()}`}
                  size="small"
                  variant="outlined"
                  onClick={() => setPago(p => ({ ...p, monto: String(b.val) }))}
                  clickable
                  sx={{ fontSize: 11 }}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Payment history */}
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <HistoryIcon sx={{ fontSize: 18, color: "text.secondary" }} />
        <Typography variant="subtitle2" fontWeight={700}>Historial de pagos</Typography>
        <Chip label={pagos.length} size="small" sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      {pagos.length === 0 ? (
        <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
          Sin pagos registrados
        </Typography>
      ) : (
        <List dense disablePadding>
          {pagos.map((p) => (
            <ListItem key={p.id} disableGutters sx={{ borderBottom: 1, borderColor: "grey.100", py: 1 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Typography variant="body2">
                  {p.metodo === "efectivo" ? "💵" : p.metodo === "transferencia" ? "🏦" : "📋"}
                </Typography>
              </ListItemIcon>
              <ListItemText
                primary={
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={700} color="success.main">
                      +{moneda} {parseFloat(p.monto).toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.fecha?.slice(0, 10)}
                    </Typography>
                  </Stack>
                }
                secondary={p.nota || p.metodo}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Associated sales */}
      <Typography variant="subtitle2" fontWeight={700} mb={1}>📦 Ventas asociadas</Typography>
      <Stack spacing={1}>
        {cliente.ventas.map((v) => (
          <Box key={v.id} sx={{ p: 1, borderRadius: 1.5, bgcolor: "background.default", border: 1, borderColor: "grey.200" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight={600}>
                {v.productoNombre || "Venta"}
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {moneda} {v.total.toLocaleString()}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {v.fecha?.slice(0, 10)}
              {v.fechaLimitePago && ` · Límite: ${v.fechaLimitePago}`}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
